// Globals
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IGeneralFaq } from '@/models/generalFaq.model';
import { FaqExportScope } from '@/models/faqImportExport.model';

// Modules
import { Header } from '@/modules/Layout';
import {
  ExportFaqsDTO,
  FaqBulkActionsBar,
  FaqTable,
  TFaqFormValues,
  useChosenFaqs,
  useExportFaqs,
  useFaqSearchFilters,
} from '@/modules/FaqCommon';
import { BulkDeleteGeneralFaqModal, CreateGeneralFaqModal, ImportGeneralFaqModal } from '../../components';

// Components
import { PlusIcon } from '@/icons';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button } from '@/ui';
import { StyledButtons, StyledContainer } from './GeneralFaqsContainerStyles';

// Store
import { useAppDispatch } from '@/store';
import { deleteGeneralFaqs, getGeneralFaqs, updateGeneralFaq } from '../../feature';

// Helpers
import { useBoolean } from '@/hooks';
import { GetGeneralFaqsDTO } from '../../dtos';
import { useGetGeneralFaqs } from '../../hooks';

const GeneralFaqsContainer = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { faqs, isLoading, pagination } = useGetGeneralFaqs();
  const { handleExportFaqs, isExporting } = useExportFaqs();
  const { chosenFaqs, rowSelection, handleResetChosenFaqs } = useChosenFaqs<IGeneralFaq>();

  const { value: isCreateOpen, setTrue: openCreate, setFalse: closeCreate } = useBoolean();
  const { value: isImportOpen, setTrue: openImport, setFalse: closeImport } = useBoolean();
  const { value: isBulkDeleteOpen, setTrue: openBulkDelete, setFalse: closeBulkDelete } = useBoolean();

  const handleSearch = useCallback(
    (filters: { question?: string; answer?: string }) => {
      dispatch(getGeneralFaqs(new GetGeneralFaqsDTO({ page: 1, ...filters })));
    },
    [dispatch],
  );

  const { filters, handleFilterChange } = useFaqSearchFilters(handleSearch);

  const handlePageChange = (page: number) => {
    dispatch(getGeneralFaqs(new GetGeneralFaqsDTO({ page, question: filters.question, answer: filters.answer })));
  };

  const handleGeneralFaqsReload = useCallback(() => {
    dispatch(
      getGeneralFaqs(
        new GetGeneralFaqsDTO({
          page: pagination?.page ?? 1,
          question: filters.question,
          answer: filters.answer,
        }),
      ),
    );
  }, [dispatch, pagination?.page, filters]);

  const handleEditFaq = async (faqId: string, values: TFaqFormValues) => {
    await dispatch(updateGeneralFaq({ ...values, faqId })).unwrap();
  };

  const handleDeleteFaq = async (faqId: string) => {
    try {
      await dispatch(deleteGeneralFaqs({ faqIds: [faqId] })).unwrap();
      handleGeneralFaqsReload();
    } catch {}
  };

  const handleExport = () => {
    handleExportFaqs(
      new ExportFaqsDTO({ scope: FaqExportScope.GENERAL, question: filters.question, answer: filters.answer }),
    );
  };

  return (
    <>
      <Header title={t('menu_general_faq')} suptitle={t('menu_faqs')}>
        <StyledButtons>
          <Button icon={<UploadOutlined />} type="default" loading={isExporting} onClick={handleExport}>
            {t('btn_faqs_export')}
          </Button>
          <Button icon={<DownloadOutlined />} type="default" onClick={openImport}>
            {t('btn_faqs_import')}
          </Button>
          <Button icon={<PlusIcon />} type="primary" onClick={openCreate}>
            {t('btn_faqs_create')}
          </Button>
        </StyledButtons>
      </Header>
      <StyledContainer>
        <FaqBulkActionsBar selectedCount={chosenFaqs.length} onDeleteSelected={openBulkDelete} />
        <FaqTable
          data={faqs}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={handlePageChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          onEditFaq={handleEditFaq}
          onDeleteFaq={handleDeleteFaq}
          rowSelection={rowSelection}
        />
      </StyledContainer>
      <CreateGeneralFaqModal isOpen={isCreateOpen} onClose={closeCreate} />
      <ImportGeneralFaqModal isOpen={isImportOpen} onClose={closeImport} />
      <BulkDeleteGeneralFaqModal
        isOpen={isBulkDeleteOpen}
        onClose={closeBulkDelete}
        chosenFaqIds={chosenFaqs}
        resetSelection={handleResetChosenFaqs}
        onDeleted={handleGeneralFaqsReload}
      />
    </>
  );
};

export default GeneralFaqsContainer;
